<script setup>
import { reactive, computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  partner: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  // AP-03: backend validation message surfaced by the parent.
  serverError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

const isEditing = computed(() => !!props.partner?.id);

const form = reactive({
  name: props.partner?.name || '',
  kind: props.partner?.kind || 'convenio',
  active: props.partner?.active ?? true,
});

// AP-03: inline validation feedback instead of a silent return + disabled button.
const clientErrors = reactive({ name: '' });

const handleSubmit = () => {
  clientErrors.name = form.name?.trim()
    ? ''
    : t('APPOINTMENTS.VALIDATION.NAME_REQUIRED');
  if (clientErrors.name) return;
  emit('submit', { ...form });
};

// H6: Focus management
const dialogRef = ref(null);
let triggerEl;
onMounted(() => {
  triggerEl = document.activeElement;
  const focusable = dialogRef.value?.querySelector(
    'input,select,button,textarea,[tabindex]:not([tabindex="-1"])'
  );
  focusable?.focus();
});
onUnmounted(() => triggerEl?.focus?.());
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    role="dialog"
    aria-modal="true"
    aria-labelledby="partner-form-title"
  >
    <div
      ref="dialogRef"
      class="bg-n-background rounded-xl shadow-xl w-full max-w-lg flex flex-col"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-strong"
      >
        <h2
          id="partner-form-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{
            isEditing
              ? t('APPOINTMENTS.PARTNERS.EDIT')
              : t('APPOINTMENTS.PARTNERS.ADD')
          }}
        </h2>
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-11"
          :aria-label="t('APPOINTMENTS.CLOSE')"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-5" aria-hidden="true" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-4">
        <!-- AP-03: backend validation message -->
        <div
          v-if="serverError"
          class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11 px-4 py-3 text-sm flex items-start gap-2"
          role="alert"
        >
          <span
            class="i-lucide-alert-circle size-4 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <span>{{ serverError }}</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.PARTNERS.FORM.NAME') }}
            <span class="text-n-ruby-9">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            :class="clientErrors.name ? 'border-n-ruby-7' : 'border-n-strong'"
            class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
            @input="clientErrors.name = ''"
          />
          <p v-if="clientErrors.name" class="mt-1 text-xs text-n-ruby-11">
            {{ clientErrors.name }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.PARTNERS.FORM.KIND') }}
          </label>
          <div class="relative">
            <select
              v-model="form.kind"
              class="!mb-0 w-full appearance-none !bg-none border border-n-strong rounded-lg pl-3 pr-9 py-2 text-sm bg-n-background text-n-slate-12 transition-colors hover:border-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            >
              <option value="convenio">
                {{ t('APPOINTMENTS.PARTNERS.KIND.CONVENIO') }}
              </option>
              <option value="seguro">
                {{ t('APPOINTMENTS.PARTNERS.KIND.SEGURO') }}
              </option>
              <option value="plano">
                {{ t('APPOINTMENTS.PARTNERS.KIND.PLANO') }}
              </option>
              <option value="outros">
                {{ t('APPOINTMENTS.PARTNERS.KIND.OUTROS') }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down absolute right-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="partner-active"
            v-model="form.active"
            type="checkbox"
            class="rounded border-n-strong"
          />
          <label
            for="partner-active"
            class="text-sm text-n-slate-12 select-none cursor-pointer"
          >
            {{ t('APPOINTMENTS.PARTNERS.FORM.ACTIVE') }}
          </label>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 rounded-lg"
          @click="$emit('close')"
        >
          {{ t('APPOINTMENTS.PARTNERS.FORM.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSubmitting"
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleSubmit"
        >
          {{ t('APPOINTMENTS.PARTNERS.FORM.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
